/* istanbul ignore file */
import React, { useState } from 'react';
import { InfoIcon } from '@chakra-ui/icons';
import { Box, Button, FormControl, FormLabel, Stack, Switch, Text } from '@chakra-ui/react';
import { isProductionEnvironment } from '@common/services';
import { NotificationStatus } from 'enums/notification-status';
import { setIsApplicationLocked } from 'state/slices/fieldSettingsSlice';
import { enqueueNotification } from 'state/slices/notificationsSlice';
import { setPermission } from 'state/slices/userPermissionSlice';
import { RootState } from 'state/store';
import { useDispatch, useSelector } from 'state/storeHooks';

import { IN_IFRAME } from 'utils/iframe';

const getRandomStatus = (): NotificationStatus => {
  const statuses = Object.values(NotificationStatus);
  return statuses[Math.floor(Math.random() * statuses.length)];
};

const LOCAL_FORM_VIEWER_ORIGIN = 'http://localhost:3002';

const isLocalHost = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';

const openLocally = () => {
  const currentUrl = new URL(window.location.href);
  const localUrl = new URL(LOCAL_FORM_VIEWER_ORIGIN);

  localUrl.pathname = currentUrl.pathname;
  localUrl.search = currentUrl.search;
  localUrl.hash = currentUrl.hash;

  window.open(localUrl.toString(), '_blank', 'noopener,noreferrer');
};

export const CustomDevTools = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const dispatch = useDispatch();

  const permissions = useSelector((state: RootState) => state.userPermission.permission);
  const unmaskPermission = permissions.viewApplicationMaskedSsnInfoAction === true;
  const shouldShowOpenLocally = IN_IFRAME && !isLocalHost(window.location.hostname);

  const toggleDevTools = () => {
    setIsOpen(!isOpen);
  };

  const handleLockToggle = () => {
    const newLockState = !isLocked;
    setIsLocked(newLockState);
    dispatch(setIsApplicationLocked(newLockState));
  };

  const handleSSNPermissionToggle = () => {
    dispatch(
      setPermission({
        ...permissions,
        viewApplicationMaskedSsnInfoAction: !unmaskPermission,
      }),
    );
  };

  if (isProductionEnvironment(process.env.NODE_ENV, window.env?.APP_ENV)) return null;

  const triggerNotification = () => {
    const randomStatus = getRandomStatus();
    const id = `dev-notification-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    dispatch(
      enqueueNotification({
        id,
        title: `${randomStatus} Notification`,
        message:
          'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        status: randomStatus,
        persist: true,
        date: new Date().toISOString(),
      }),
    );
  };

  return (
    <>
      <InfoIcon
        boxSize="16px"
        position="fixed"
        bottom="10px"
        left="10px"
        zIndex="9999"
        onClick={toggleDevTools}
        aria-label="Open DevTools"
      />

      {isOpen && (
        <Box
          position="fixed"
          bottom="30px"
          left="30px"
          zIndex="9999"
          p="4"
          bg="gray.700"
          color="white"
          borderRadius="md"
          boxShadow="md"
          width="300px"
          height="280px"
          overflow="auto"
        >
          <Text fontWeight="bold" mb={2}>
            Dev tools
          </Text>

          <Stack spacing={4}>
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="lock-switch" mb="0" color="white">
                Lock Application Fields
              </FormLabel>
              <Switch id="lock-switch" isChecked={isLocked} onChange={handleLockToggle} />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="ssn-permission-switch" mb="0" color="white">
                Can Unmask SSN
              </FormLabel>
              <Switch
                id="ssn-permission-switch"
                isChecked={unmaskPermission}
                onChange={handleSSNPermissionToggle}
              />
            </FormControl>

            <Button size="sm" onClick={triggerNotification}>
              Trigger Random Notification
            </Button>

            {shouldShowOpenLocally && (
              <Button size="sm" onClick={openLocally}>
                Open Locally
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </>
  );
};
